import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Tracks user activity (mouse, keyboard, touch, scroll) and triggers
 * a warning + logout after a configurable inactivity period.
 *
 * - `timeoutMs`   : total inactivity allowed before logout
 * - `warningMs`   : how long before logout the warning modal appears
 */
export interface UseIdleLogoutOptions {
  enabled: boolean;
  timeoutMs?: number;
  warningMs?: number;
  onLogout: () => void | Promise<void>;
}

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
  'click',
] as const;

const STORAGE_KEY = 'lapyno_last_activity_ts';

export function useIdleLogout({
  enabled,
  timeoutMs = 30 * 60 * 1000,
  warningMs = 60 * 1000,
  onLogout,
}: UseIdleLogoutOptions) {
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(Math.floor(warningMs / 1000));
  const lastActivityRef = useRef<number>(Date.now());
  const tickRef = useRef<number | null>(null);

  const recordActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    try {
      localStorage.setItem(STORAGE_KEY, String(now));
    } catch {
      // ignore
    }
    if (warningOpen) setWarningOpen(false);
  }, [warningOpen]);

  const stayConnected = useCallback(() => {
    recordActivity();
    setWarningOpen(false);
  }, [recordActivity]);

  useEffect(() => {
    if (!enabled) return;

    // Initial sync from storage (multi-tab)
    try {
      const stored = Number(localStorage.getItem(STORAGE_KEY) || '0');
      if (stored > lastActivityRef.current) lastActivityRef.current = stored;
      else recordActivity();
    } catch {
      recordActivity();
    }

    const handler = () => recordActivity();
    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, handler, { passive: true })
    );

    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        const v = Number(e.newValue);
        if (!Number.isNaN(v) && v > lastActivityRef.current) {
          lastActivityRef.current = v;
          if (warningOpen) setWarningOpen(false);
        }
      }
    };
    window.addEventListener('storage', onStorage);

    const tick = () => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= timeoutMs) {
        setWarningOpen(false);
        void onLogout();
        return;
      }
      const remaining = timeoutMs - idle;
      if (remaining <= warningMs) {
        setSecondsRemaining(Math.max(1, Math.ceil(remaining / 1000)));
        setWarningOpen(true);
      } else if (warningOpen) {
        setWarningOpen(false);
      }
    };

    tickRef.current = window.setInterval(tick, 1000);
    tick();

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, handler));
      window.removeEventListener('storage', onStorage);
      if (tickRef.current) window.clearInterval(tickRef.current);
    };
  }, [enabled, timeoutMs, warningMs, onLogout, recordActivity, warningOpen]);

  return { warningOpen, secondsRemaining, stayConnected };
}